import React, { useState } from 'react';
import {
  Eye,
  EyeOff,
  User,
  Lock,
  ArrowRight,
  Target,
  BarChart3,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { login } from '../services/auth';
import marplyLogo from '../assets/marply-logo.png';

const Feature = ({ icon: Icon, children }) => (
  <li className="flex items-center gap-3">
    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15">
      <Icon className="h-5 w-5" />
    </span>
    <span className="text-stone-200/90">{children}</span>
  </li>
);

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      onLogin(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen flex font-inter">
      {/* ---------- Painel da marca (esquerda, only lg) ---------- */}
      <div
        className="relative hidden lg:flex lg:w-1/2 overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg,#1c1917 0%,#4a1620 55%,#7a2433 100%)' }}
      >
        {/* blobs decorativos */}
        <div className="pointer-events-none absolute -top-28 -left-20 h-96 w-96 rounded-full bg-[#7a2433]/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-16 h-96 w-96 rounded-full bg-amber-400/10 blur-3xl" />
        {/* grade pontilhada sutil */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />

        <div className="relative z-10 flex w-full flex-col justify-between p-12">
          {/* logo (em chip claro p/ a logo escura aparecer) */}
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-white px-4 py-2.5 shadow-lg">
              <img src={marplyLogo} alt="Marply" className="h-8 w-auto" />
            </div>
            <span className="text-sm text-stone-300">Indústria de Compensados</span>
          </div>

          {/* headline */}
          <div className="max-w-md space-y-6">
            <div>
              <h1 className="text-4xl font-bold leading-tight">
                Gestão estratégica para a indústria de compensados.
              </h1>
              {/* toque da cor secundária da marca (amarelo Marply) */}
              <span className="mt-4 block h-1 w-14 rounded-full" style={{ backgroundColor: '#e8d833' }} />
            </div>
            <p className="text-lg text-stone-200/80">
              Acompanhe perspectivas, objetivos e indicadores da fábrica num só
              lugar — do mapa estratégico à performance global.
            </p>
            <ul className="space-y-4 pt-2">
              <Feature icon={Target}>Mapa e fluxo estratégico</Feature>
              <Feature icon={BarChart3}>Balanced Scorecard por período</Feature>
              <Feature icon={TrendingUp}>Performance ponderada e tendências</Feature>
            </ul>
          </div>

          <p className="text-xs text-stone-400">
            © {year} Marply · Indústria de Compensados · Acesso restrito
          </p>
        </div>
      </div>

      {/* ---------- Painel do formulário (direita) ---------- */}
      <div className="flex flex-1 items-center justify-center bg-gradient-to-br from-stone-50 to-stone-100 p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* logo mobile */}
          <div className="mb-8 flex justify-center lg:hidden">
            <img src={marplyLogo} alt="Marply" className="h-12 w-auto" />
          </div>

          <div className="rounded-3xl bg-white/90 p-8 shadow-xl ring-1 ring-stone-900/5 backdrop-blur-xl sm:p-10">
            {/* logo no topo do card (desktop) */}
            <img src={marplyLogo} alt="Marply" className="mb-6 hidden h-11 w-auto lg:block" />

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-stone-900">Bem-vindo de volta</h2>
              <p className="mt-1 text-sm text-stone-500">
                Entre com suas credenciais para acessar o painel.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 px-4 py-3 ring-1 ring-red-200">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Usuário */}
              <div>
                <label htmlFor="username" className="mb-1.5 block text-sm font-medium text-stone-700">
                  Usuário
                </label>
                <div className="group relative">
                  <User className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400 transition-colors group-focus-within:text-[#7a2433]" />
                  <input
                    id="username"
                    type="text"
                    required
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Seu usuário"
                    className="w-full rounded-xl border border-stone-200 bg-stone-50 py-3 pl-11 pr-4 text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-[#7a2433] focus:bg-white focus:ring-4 focus:ring-[#7a2433]/15"
                  />
                </div>
              </div>

              {/* Senha */}
              <div>
                <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-stone-700">
                  Senha
                </label>
                <div className="group relative">
                  <Lock className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400 transition-colors group-focus-within:text-[#7a2433]" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Sua senha"
                    className="w-full rounded-xl border border-stone-200 bg-stone-50 py-3 pl-11 pr-11 text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-[#7a2433] focus:bg-white focus:ring-4 focus:ring-[#7a2433]/15"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 transition-colors hover:text-stone-600"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Botão */}
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white shadow-lg shadow-[#7a2433]/25 outline-none transition hover:brightness-110 focus:ring-4 focus:ring-[#7a2433]/30 disabled:cursor-not-allowed disabled:opacity-60"
                style={{ background: 'linear-gradient(90deg,#7a2433,#5c1a26)' }}
              >
                {loading ? (
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <>
                    Entrar
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-stone-400">
            © {year} Marply · Indústria de Compensados
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
